import React, { useState } from "react";
import { Delete, Edit, Redo, Share as ShareIcon } from "@mui/icons-material";
import { ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { useUpdateReportMutation } from "fond/api";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import Share from "fond/share/Share";
import { Report } from "fond/types";
import { Actions } from "fond/utils/permissions";
import { replaceAllSpecialCharacters } from "fond/utils/strings";
import { required } from "fond/utils/validation";
import MoveReportDialog from "fond/views/Report/MoveReportDialog";
import RemoveReportDialog from "fond/views/Report/RemoveReportDialog";
import { RenameModal } from "fond/widgets";

import StarMenuItem from "./StarMenuItem";
import { CommonItemMenuProps } from "./types";

interface IProps extends CommonItemMenuProps {
  report: Report;
}

type ReportModal = "delete" | "copy" | "move" | "rename" | "share";
const ReportRowMenu: React.FC<IProps> = ({ report, onMenuClose, anchorEl, source }) => {
  const [showModal, setShowModal] = useState<ReportModal>();
  const { enqueueSnackbar } = useSnackbar();
  const [updateReport] = useUpdateReportMutation();

  const canDeleteReport = usePermissionCheck(Actions.REPORT_DELETE, report.Permission?.Level);
  const canMoveReport = usePermissionCheck(Actions.REPORT_MOVE, report.Permission?.Level);
  const canRenameReport = usePermissionCheck(Actions.REPORT_RENAME, report.Permission?.Level);
  const canShareReport = usePermissionCheck(Actions.REPORT_SHARE_VIEW, report.Permission?.Level);

  const isSubReport = report?.MultiReport;

  /**
   * Generic Callback function that updates any of the modal open states and closes the menu
   */
  const openModal = (modalType: ReportModal) => (event: React.MouseEvent<EventTarget>) => {
    onMenuClose(event);
    setShowModal(modalType);
  };

  const closeModal = () => {
    setShowModal(undefined);
  };

  const handleRenameSubmit = async (value: { name: string }) => {
    if (value.name !== report.Name) {
      try {
        analytics.track("Report.Report Row.Renamed report", { reportId: report.ID });
        await updateReport({ ID: report.ID, Name: value.name }).unwrap();
      } catch {
        enqueueSnackbar("Report rename failed. Please try again...", { variant: "error" });
      } finally {
        closeModal();
      }
    }
  };

  return (
    <>
      <Menu
        data-testid={`project-list-item-menu-${replaceAllSpecialCharacters(report.Name, "")}`}
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {!isSubReport && (
          <MenuItem aria-label="Rename report" data-testid="rename-report-button" onClick={openModal("rename")} disabled={!canRenameReport}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Rename</Typography>
          </MenuItem>
        )}

        {!isSubReport && (
          <MenuItem aria-label="Move report" data-testid="move-report-button" onClick={openModal("move")} disabled={!canMoveReport}>
            <ListItemIcon>
              <Redo fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Move</Typography>
          </MenuItem>
        )}

        <MenuItem aria-label="Share report" data-testid="share-report-button" onClick={openModal("share")} disabled={!canShareReport}>
          <ListItemIcon>
            <ShareIcon />
          </ListItemIcon>
          <Typography variant="inherit">Share</Typography>
        </MenuItem>

        {!isSubReport && (
          <MenuItem aria-label="Delete report" data-testid="delete-report-button" onClick={openModal("delete")} disabled={!canDeleteReport}>
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            <Typography variant="inherit">Delete</Typography>
          </MenuItem>
        )}

        <StarMenuItem onMenuClose={onMenuClose} resource={report} source={source} />
      </Menu>

      {showModal === "delete" && <RemoveReportDialog report={report} closeModalCallback={closeModal} />}
      {showModal === "move" && <MoveReportDialog report={report} onClose={closeModal} />}
      {showModal === "rename" && (
        <RenameModal
          entity={report}
          title="Rename report"
          defaultValue={report.Name || "Untitled report"}
          onRename={(formData) => handleRenameSubmit(formData)}
          onClose={closeModal}
          onValidate={required}
        />
      )}
      {showModal === "share" && <Share resource={report} onClose={closeModal} />}
    </>
  );
};

export default ReportRowMenu;
